import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Calculator, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useConfig, whatsappHref } from "@/lib/config";

const BHK_BASE = {
  "1 RK": 6000, "1 BHK": 9000, "2 BHK": 14000, "3 BHK": 20000,
  "4 BHK / Villa": 28000, "Office / Commercial": 22000,
};
const QUALITY_MULT = { Standard: 1.0, Premium: 1.25, Express: 1.45 };
const CITIES = ["Noida", "Greater Noida", "Ghaziabad", "Gurugram", "Faridabad", "Delhi", "Other NCR", "Intercity"];

export default function PriceCalculator({ compact = false }) {
  const cfg = useConfig();
  const [bhk, setBhk] = useState("2 BHK");
  const [quality, setQuality] = useState("Standard");
  const [fromCity, setFromCity] = useState("Noida");
  const [toCity, setToCity] = useState("Gurugram");
  const [distance, setDistance] = useState(35);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);

  const computeEstimate = () => {
    const base = BHK_BASE[bhk] || 12000;
    const mult = QUALITY_MULT[quality] || 1;
    const distFactor = 1 + Math.min(Math.max(distance, 0), 500) / 100;
    const low = Math.round((base * mult * distFactor) / 500) * 500;
    const high = Math.round((low * 1.35) / 500) * 500;
    return { low, high, label: `₹${low.toLocaleString("en-IN")} – ₹${high.toLocaleString("en-IN")}` };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !/^[0-9]{10}$/.test(phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter your name and a 10-digit phone number");
      return;
    }
    const est = computeEstimate();
    setEstimate(est);
    setLoading(true);
    try {
      await api.post("/leads", {
        name, phone, from_city: fromCity, to_city: toCity, bhk,
        estimated_price: est.label,
        notes: `Quality: ${quality}, Distance: ${distance} km`,
        source: "calculator",
      });
      toast.success(`Your estimate: ${est.label}. Our team will confirm within 5 minutes!`);
    } catch (err) {
      toast.error("Could not save. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const wa = () => {
    const est = estimate || computeEstimate();
    const msg = `Hi Maa Vindhyavasini Packers Movers & Transport! I need a quote.\nBHK: ${bhk}\nFrom: ${fromCity}\nTo: ${toCity}\nDistance: ${distance} km\nQuality: ${quality}\nMy estimate: ${est.label}\nName: ${name || "-"}\nPhone: ${phone || "-"}`;
    window.open(whatsappHref(cfg.whatsapp_number, msg), "_blank");
  };

  return (
    <Card className={`p-6 sm:p-7 bg-white shadow-2xl border-2 border-orange-100 rounded-2xl ${compact ? "" : "lg:w-[420px]"}`} data-testid="price-calculator">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-lg bg-[#F97316] grid place-items-center text-white">
          <Calculator className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-display font-extrabold text-lg text-[#0F172A]">Instant Price Calculator</h3>
          <p className="text-xs text-slate-500">Get an estimate in 10 seconds — no spam</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600">Home Size</Label>
            <Select value={bhk} onValueChange={setBhk}>
              <SelectTrigger className="mt-1" data-testid="calculator-bhk-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(BHK_BASE).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Service Level</Label>
            <Select value={quality} onValueChange={setQuality}>
              <SelectTrigger className="mt-1" data-testid="calculator-quality-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(QUALITY_MULT).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-600">From City</Label>
            <Select value={fromCity} onValueChange={setFromCity}>
              <SelectTrigger className="mt-1" data-testid="calculator-from-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-600">To City</Label>
            <Select value={toCity} onValueChange={setToCity}>
              <SelectTrigger className="mt-1" data-testid="calculator-to-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Approx Distance (km)</Label>
          <Input type="number" min="1" max="2000" value={distance} onChange={(e) => setDistance(Number(e.target.value) || 0)} className="mt-1" data-testid="calculator-distance-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Your Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="calculator-name-input" />
          <Input placeholder="Phone (10-digit)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="calculator-phone-input" />
        </div>

        {estimate && (
          <div className="rounded-xl bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 p-3 text-center">
            <p className="text-xs text-slate-600">Estimated Cost</p>
            <p className="font-display text-2xl font-extrabold text-[#F97316]" data-testid="calculator-estimate-display">{estimate.label}</p>
            <p className="text-[10px] text-slate-500">*Final quote after quick site visit</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button type="submit" disabled={loading} className="bg-[#1E3A8A] hover:bg-[#0F172A] text-white w-full" data-testid="calculator-submit-button">
            {loading ? "Calculating..." : "Get Estimate"}
          </Button>
          <Button type="button" onClick={wa} className="bg-[#22C55E] hover:bg-[#16A34A] text-white w-full" data-testid="calculator-whatsapp-button">
            <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
          </Button>
        </div>
      </form>
    </Card>
  );
}
